import {
  type ClearanceFrame,
  clearanceSampleStride,
  resolveClearanceAdjustments,
  type TerrainSampler,
} from "./cameraClearance";
import type { CameraMode, CameraPlan, CameraPose, CameraShot, RouteGeometry } from "./cameraPlan";
import { cumulativeMeters, type LngLat, pointAtMeters, routeLengthMeters } from "./cameraRoute";
import {
  buildCameraSpline,
  evalSpline,
  MODE_SMOOTHING_SIGMAS,
  planDurationMs,
  sigmaFramesForMeters,
  smoothPositionSeries,
  smoothSeries,
  unwrapBearings,
} from "./cameraSpline";
import { viewportGroundSpanMeters } from "./cameraZoom";

/**
 * frameCountFor is the per-frame grid size both resolveCameraTrack and
 * resolveRouteMarkers derive from a plan: `ceil(planDurationMs / 1000 * fps)`,
 * floored at one. The count keys off the plan's full wall-clock — the target
 * duration plus every shot's hold — because the encoder stamps each frame at a
 * fixed 1/fps: only a grid sized to the whole flight makes the exported file
 * last as long as the preview rehearsal and dwell on holds for their real
 * duration. Held as one helper so the pose array and the route-marker array are
 * always the same length and align frame-for-frame.
 */
const frameCountFor = (plan: CameraPlan): number =>
  Math.max(1, Math.ceil((planDurationMs(plan) / 1_000) * plan.output.fps));

/**
 * MODE_LEAD_SCREEN_FRACTIONS is the per-mode athlete lead expressed as a fraction
 * of the viewport's ground span: on a following frame the camera looks this far
 * along the route ahead of the athlete, so the trail the rider is about to take
 * opens in frame (on a hairpin the camera looks toward the upcoming switchback)
 * and the athlete rides the lower third rather than dead centre. `chase` leads
 * the most (the athlete pinned low, the most road ahead — the follow-cam
 * grammar), `cinematic` close behind, `drone` the least of the followers (a wide
 * aerial already shows the trail), `helicopter` none at all (its value-bearing
 * lateral track wants the athlete dead-centre; the lead is follow-gated anyway),
 * `overview` light, `smart`/`director` between. The lead ramps in from the route
 * start and collapses at the finish so the endpoints stay pinned. Held as a
 * constant table so a mode's framing is one literal to tune.
 */
const MODE_LEAD_SCREEN_FRACTIONS: Record<CameraMode, number> = {
  chase: 0.26,
  cinematic: 0.24,
  director: 0.16,
  drone: 0.08,
  helicopter: 0,
  overview: 0.12,
  smart: 0.16,
};

/**
 * LEAD_MAX_METERS caps the along-route athlete lead regardless of zoom. A very
 * wide frame (low zoom) would otherwise lead hundreds of metres ahead and lose the
 * athlete off the back of the frame; ~400 m keeps the lead readable as "looking up
 * the trail" rather than abandoning the rider.
 */
const LEAD_MAX_METERS = 400;

/**
 * MAX_MARKER_OFFCENTER_FRACTION is the largest fraction of the viewport's ground
 * span the athlete may sit off the look-at centre before the divergence budget
 * pulls the centre back. ~0.32 keeps the rider comfortably inside the frame (the
 * lead already rides them low) and bounds a high-zoom director shot, whose tight
 * framing would otherwise let the centre and the marker drift far apart on a turn.
 */
const MAX_MARKER_OFFCENTER_FRACTION = 0.32;

/**
 * MODE_BEARING_MIN_SMOOTH_SECONDS is a per-mode time-domain floor (in seconds) on
 * the heading smoothing window, applied alongside the distance-domain σ. The
 * distance σ keeps the smoothing duration-invariant, but on a switchback the
 * heading can swing 180° within a very short stretch of trail, which the
 * distance σ alone would barely smooth; a seconds floor guarantees the heading
 * still takes at least this long to swing no matter how compressed the turn is in
 * route-distance. `drone` floors the longest (the floatiest heading), then
 * `helicopter` (a heavy broadcast gimbal on switchbacks) and `overview`;
 * `chase` floors the shortest (the hard action-cam bank), just under
 * `cinematic`/`director`; `smart` between.
 */
const MODE_BEARING_MIN_SMOOTH_SECONDS: Record<CameraMode, number> = {
  chase: 0.3,
  cinematic: 0.35,
  director: 0.35,
  drone: 0.9,
  helicopter: 0.8,
  overview: 0.6,
  smart: 0.45,
};

/**
 * MODE_HOLD_REVEAL_ZOOM is how many zoom levels wider than the establishing
 * keyframe a mode's intro hold opens on before pushing in to land exactly on the
 * keyframe at the hold's end. It is the deliberate opening push-in: `overview`
 * opens the widest (the grand map reveal), `drone` a grand aerial, `helicopter`
 * a broadcast pass, `cinematic` wide, `chase` moderate, `smart`/`director` a
 * gentle settle. Applied as a negative (wider) zoom offset only, so the clearance
 * pass that runs after it (and only lowers zoom) never fights it.
 */
const MODE_HOLD_REVEAL_ZOOM: Record<CameraMode, number> = {
  chase: 0.5,
  cinematic: 0.66,
  director: 0.4,
  drone: 0.9,
  helicopter: 0.8,
  overview: 1.1,
  smart: 0.42,
};

/**
 * MODE_HOLD_SETTLE_ZOOM is how many zoom levels wider than the travel framing a
 * mode's outro hold settles out to by the final frame: the flight eases off the
 * athlete into a closing wide shot. `overview` settles the widest (back out to
 * the map), `drone` and `helicopter` wide, `cinematic` moderate, `chase` and
 * `smart`/`director` a touch. A negative (wider) zoom offset only, same as the
 * intro reveal.
 */
const MODE_HOLD_SETTLE_ZOOM: Record<CameraMode, number> = {
  chase: 0.35,
  cinematic: 0.42,
  director: 0.3,
  drone: 0.6,
  helicopter: 0.55,
  overview: 0.9,
  smart: 0.3,
};

/**
 * MID_HOLD_REVEAL_ZOOM is the gentle widen a user-placed mid-route hold breathes
 * out and back to over its dwell (a `-sin²(πu)` arc, zero at both edges so it
 * joins the travel framing seamlessly on either side). Far subtler than the
 * intro/outro beats — a mid-flight pause should feel like the camera lingering,
 * not a dramatic re-frame.
 */
const MID_HOLD_REVEAL_ZOOM = 0.2;

/**
 * BUDGET_KNEE_FRACTION is where the divergence soft-knee begins, as a fraction of
 * the budget: below it the centre→marker offset passes through untouched, above it
 * the knee compresses. BUDGET_KNEE_SPAN sets both the tanh width and the residual
 * headroom to the 1.0·budget asymptote, so the offset eases from 0.8·budget toward
 * the budget rather than clipping at a hard edge.
 */
const BUDGET_KNEE_FRACTION = 0.8;
const BUDGET_KNEE_SPAN = 0.2;

/**
 * EDGE_EASE_SUBNODES is how many intermediate timeline nodes the first and last
 * travel legs are subdivided into so their progress can accelerate out of the
 * start and decelerate into the finish. A linear leg would leave the intro hold
 * (and arrive at the outro) at full travel speed — the camera, the marker, and
 * every overlay number stopping dead on the final frame, the single most
 * "screen recording, not film" tell. Eight sub-nodes make the trapezoidal
 * profile visually exact under progressAtTime's linear interpolation; the leg's
 * total wall-clock is untouched, so holds land at the same time and
 * planDurationMs is invariant.
 */
const EDGE_EASE_SUBNODES = 8;

/**
 * EDGE_EASE_WINDOW_FRACTION is the share of an edge travel leg spent
 * accelerating from standstill (first leg) or decelerating into it (last leg) —
 * a trapezoidal velocity profile, the shape of a professional camera move. The
 * cruise velocity compensates the ramp so the leg still covers its span in its
 * unchanged wall-clock window (`1 / (1 − a/2)` ≈ 1.18×), deliberately gentle: a
 * full-leg quadratic would peak the per-frame sampling stride at 2× and push a
 * hairpin's smoothed heading shoulder past the per-mode whip budget, whereas the
 * trapezoid's bounded cruise keeps every downstream per-frame delta within the
 * budgets the stability suite pins.
 */
const EDGE_EASE_WINDOW_FRACTION = 0.3;

/**
 * REPRESENTATIVE_TRAVEL_FRACTION is how far into the travelling portion of a flight
 * (past the intro establishing beat) a parked preview sits, as a fraction of the
 * flight remaining after that beat. A fifth of the way in is comfortably clear of the
 * intro hold and the edge-ease ramp, so the parked frame shows the mode's settled
 * travelling framing — the whole point of parking past the reveal rather than on
 * frame 0.
 */
const REPRESENTATIVE_TRAVEL_FRACTION = 0.2;

const TO_RAD = Math.PI / 180;
const METERS_PER_DEGREE_LAT = 111_320;

/**
 * resolveFrameProgressions is the per-frame route-progress fraction sequence both
 * resolveCameraTrack and resolveRouteMarkers walk: for each of the
 * `frameCountFor(plan)` frames it maps the frame's wall-clock time
 * (`(frame / lastFrame) * planDurationMs`, honouring every shot's hold + speedScale
 * through buildTimeline + progressAtTime) to the route fraction at that instant.
 * Held as one helper so every per-frame array a plan resolves — the pose track, the
 * moving marker, the profile samples — is the same length and aligns
 * frame-for-frame: `fractions[i]` is the progress the camera, the marker, and the
 * overlay all read at frame i. An empty-shots plan resolves through the same
 * full-route follow shots, so the sequence sweeps 0→1 even on a freshly-seeded
 * director plan.
 */
export const resolveFrameProgressions = (plan: CameraPlan): number[] => {
  const shots = plan.shots.length > 0 ? plan.shots : fullRouteFollowShots(plan);
  const timeline = buildTimeline(shots, plan.targetDurationMs);
  const totalMs = planDurationMs(plan);

  const frameCount = frameCountFor(plan);
  const lastFrame = frameCount - 1;

  const fractions = new Array<number>(frameCount);
  for (let frame = 0; frame < frameCount; frame += 1) {
    const timeMs = lastFrame === 0 ? 0 : (frame / lastFrame) * totalMs;
    fractions[frame] = progressAtTime(timeline, timeMs);
  }

  return fractions;
};

/**
 * resolveCameraTrack turns a Camera Plan and its route into the render-free
 * per-frame pose array the map flies and records. The frame count is
 * `ceil(planDurationMs / 1000 * fps)` (the holds extend the count along with the
 * wall-clock), so an encoder stamping one frame per 1/fps reproduces the plan's
 * full duration — the exported file lasts exactly as long as the preview
 * rehearsal, holds dwelt at their real length. Each frame's wall-clock time
 * is mapped to a route-progress fraction by a timeline that honours every shot's
 * `holdMs` (progress freezes at the shot while the camera dwells) and `speedScale`
 * (a leg's share of the travelling time scales inversely, so a >1 scale spends
 * that leg faster); the pose at that progress comes from the smoothed spline. An
 * empty-shots plan (a freshly-seeded `director` plan) resolves to a single
 * full-route follow flight so a preview is never blank.
 *
 * Smoothing is distance-domain: each mode's σ is a route-DISTANCE (metres),
 * converted to frame samples per route via sigmaFramesForMeters, so the window
 * covers the same fraction of the trail no matter the duration — a 10 s and a 60 s
 * flight over the same route smooth the same route-distance and are equally
 * accurate, rather than a fixed-seconds window over-smoothing the short one.
 *
 * When a `sampler` is supplied (the tool layer injects the map's DEM readout), a
 * clearance pass runs over the smoothed framing channels: it derives the camera eye
 * per frame and, where the eye would sink into the terrain or the sightline to the
 * trail would clip a ridge, repositions the camera to clear it — tilting toward
 * top-down (lowering pitch, which slides the eye over the target) to clear an
 * occluder, and widening the view (lowering zoom) where a tilt alone cannot. It
 * returns a per-frame zoom AND pitch adjustment, each only ever lowering its
 * channel; the final framing is the min of the mode-smoothed value and the
 * clearance value on both channels. Both clearance channels are smoothed with the
 * same anticipation window, so the reframing leads the peak and never jolts the
 * motion. Without a sampler (a render-free test, no map, the embed) the output is
 * identical to the un-cleared track on every channel.
 *
 * `clearanceStride` decimates the clearance pass's per-frame frustum solve (see
 * ClearanceOptions.sampleStride): a preview passes a coarse stride so a long flight
 * resolves without stalling, while a record passes 1 (or omits it) to solve every
 * frame so the exported artifact is byte-unchanged. Omitted, the clearance runs at
 * full density.
 */
export const resolveCameraTrack = (
  plan: CameraPlan,
  route: RouteGeometry,
  sampler?: TerrainSampler,
  clearanceStride?: number,
): CameraPose[] => {
  const shots = plan.shots.length > 0 ? plan.shots : fullRouteFollowShots(plan);
  const spline = buildCameraSpline(shots, route, plan.mode);
  const timeline = buildTimeline(shots, plan.targetDurationMs);
  const totalMs = planDurationMs(plan);

  const fps = plan.output.fps;
  const line = route.line.map((coordinate): LngLat => [coordinate[0], coordinate[1]]);
  const cumulative = cumulativeMeters(line);
  const routeMeters = cumulative[cumulative.length - 1] ?? 0;

  const fractions = resolveFrameProgressions(plan);
  const frameCount = fractions.length;
  const lastFrame = frameCount - 1;
  // Each output frame maps to the smoothed u-grid series at the fractional sample
  // `fraction * lastFrame`: a travelling frame lands between two u-samples, a held
  // frame (a shared fraction) lands on the same sample as its neighbours and so
  // reads one constant smoothed value. On a hold-free, speedScale-1 plan the
  // interior legs are the identity (positions[i] === i); the first and last legs
  // land between samples because their progress is edge-eased (accelerating out of
  // the start, decelerating into the finish), which this mapping carries into
  // every per-frame array at once.
  const positions = fractions.map((fraction) => fraction * lastFrame);

  // Sample the raw channels on a UNIFORM progress grid `u_k = k / lastFrame` rather
  // than on the (hold-warped) per-frame fractions. The Gaussian below then runs on a
  // grid whose samples are evenly spaced in route progress, which is exactly the
  // assumption sigmaFramesForMeters encodes (frameCount samples across the route) —
  // so the smoothing window spans the route-distance it claims to, and hold frames
  // cannot bleed travel motion into the dwell. The athlete lead is applied here, at
  // the raw-centre stage, so the mode's centre σ smooths the lead as one motion with
  // the rest of the path.
  const leadFraction = MODE_LEAD_SCREEN_FRACTIONS[plan.mode];
  const rawBaseLngs = new Array<number>(frameCount);
  const rawBaseLats = new Array<number>(frameCount);
  const rawLeadLngs = new Array<number>(frameCount);
  const rawLeadLats = new Array<number>(frameCount);
  const rawPitches = new Array<number>(frameCount);
  const rawZooms = new Array<number>(frameCount);
  const rawBearings = new Array<number>(frameCount);
  for (let k = 0; k < frameCount; k += 1) {
    const progress = lastFrame === 0 ? 0 : k / lastFrame;
    const pose = evalSpline(spline, progress);
    const lat = pose.center[1];
    const metersFromStart = progress * routeMeters;
    const base = pointAtMeters(line, metersFromStart, cumulative);
    // Lead the camera along the route ahead of the athlete by a fraction of the
    // viewport's ground span, gated by the follow weight (no lead on a locked or
    // value shot), capped in metres, and ramped in from the start so the lead is 0
    // at the route start. pointAtMeters clamps past the route end, so the lead
    // collapses to 0 as the flight reaches the finish. The lead is held as a
    // displacement off the base route point (0 at both ends by construction) so the
    // centre smoothing can endpoint-pin the base path and Gaussian-smooth the lead
    // without the pin eating the lead plateau.
    const leadM = Math.min(
      LEAD_MAX_METERS,
      metersFromStart,
      pose.followWeight * leadFraction * viewportGroundSpanMeters(pose.zoom, lat),
    );
    const led = pointAtMeters(line, metersFromStart + leadM, cumulative);
    rawBaseLngs[k] = base[0];
    rawBaseLats[k] = base[1];
    rawLeadLngs[k] = led[0] - base[0];
    rawLeadLats[k] = led[1] - base[1];
    rawPitches[k] = pose.pitch;
    rawZooms[k] = pose.zoom;
    rawBearings[k] = pose.bearing;
  }

  // Smooth the framing channels over the uniform progress grid with the mode's σ
  // (in route metres) converted to frame samples for THIS route and frame count, so
  // the window spans a fixed route-distance whether the flight is short or long. The
  // heading additionally takes a time-domain floor: on a switchback the bearing can
  // swing 180° within a few metres of trail, which the distance σ alone barely
  // smooths, so the window is floored to at least MODE_BEARING_MIN_SMOOTH_SECONDS of
  // frames (and re-capped at a third of the grid). The centre channel — which the
  // duration-invariance test measures — keeps the pure distance σ.
  const sigmas = MODE_SMOOTHING_SIGMAS[plan.mode];
  const centerSigmaFrames = sigmaFramesForMeters(sigmas.center, frameCount, routeMeters);
  const pitchZoomSigmaFrames = sigmaFramesForMeters(sigmas.pitchZoom, frameCount, routeMeters);
  const bearingSigmaFrames = bearingSigmaFramesFor(plan.mode, frameCount, routeMeters, fps);
  // The base path is endpoint-pinned (frame 0 = route start, last = finish) and
  // glides over high-frequency wiggle; the lead displacement is Gaussian-smoothed
  // (so its direction eases through turns) then multiplied by an endpoint taper that
  // forces it to exactly 0 at the first and last frames without a subtractive chord
  // that would halve the interior lead. The taper ramps over the centre window, so
  // the look-at point leads the athlete across the interior while the endpoints stay
  // pinned to the route. Both channels use the centre σ, so the lead is smoothed as
  // one motion with the path.
  const leadTaper = endpointTaper(frameCount, Math.ceil(centerSigmaFrames * 3));
  const smoothedCenterLngs = addSeries(
    smoothPositionSeries(rawBaseLngs, centerSigmaFrames),
    multiplySeries(smoothSeries(rawLeadLngs, centerSigmaFrames), leadTaper),
  );
  const smoothedCenterLats = addSeries(
    smoothPositionSeries(rawBaseLats, centerSigmaFrames),
    multiplySeries(smoothSeries(rawLeadLats, centerSigmaFrames), leadTaper),
  );
  const smoothedPitches = smoothSeries(rawPitches, pitchZoomSigmaFrames);
  const smoothedZooms = smoothSeries(rawZooms, pitchZoomSigmaFrames);
  // Smooth the heading in the UNWRAPPED angle domain rather than by a circular
  // (cos/sin) mean: across a switchback the raw tangent reverses ~180°, and a
  // circular mean over a window straddling that reversal cancels to an unstable
  // direction that actually whips the smoothed heading. Unwrapping accumulates the
  // shortest signed arc into a continuous series the Gaussian genuinely low-passes,
  // then the result is folded back into 0..360 — the same unwrap the keyframe
  // bearing spline uses, so a reversal is damped instead of snapped.
  const smoothedBearings = smoothSeries(unwrapBearings(rawBearings), bearingSigmaFrames).map(
    (value) => ((value % 360) + 360) % 360,
  );

  // Map the smoothed u-grid series down to the per-frame grid: a travelling frame
  // interpolates between two u-samples, a held frame reads its single constant
  // sample. Bearing is sampled circularly so the interpolation crosses 0/360 the
  // short way.
  const centerLngs = sampleSeriesAt(smoothedCenterLngs, positions);
  const centerLats = sampleSeriesAt(smoothedCenterLats, positions);
  const pitches = sampleSeriesAt(smoothedPitches, positions);
  const zooms = sampleSeriesAt(smoothedZooms, positions);
  const bearings = sampleCircularSeriesAt(smoothedBearings, positions);

  // Hold shaping (zoom only): on the now-constant hold windows, add a deliberate
  // eased push-in (intro) / settle-out (outro) / breathe (mid) so a dwell reads as
  // a directed beat rather than a frozen frame. Offsets are negative (wider) only,
  // so the clearance pass that follows — which only lowers zoom — never fights them.
  applyHoldZoomShaping(zooms, shots, plan.mode, totalMs, lastFrame);

  // Budget clamp (centre only): pull the look-at point back toward the athlete with
  // a C1 soft-knee whenever the planar offset exceeds the per-frame budget, so the
  // rider never drifts past MAX_MARKER_OFFCENTER_FRACTION of the frame (bounding a
  // high-zoom director shot and an extreme hairpin). Inputs are already smooth and
  // the knee is C1, so no re-smoothing is needed. The markers ride the same u-grid,
  // so the dot freezes through a hold exactly as the centre does.
  const markers = resolveMarkerSeries(plan, route);
  clampCenterToMarkerBudget(centerLngs, centerLats, markers, zooms);

  // Run the DEM clearance pass over the shaped + clamped channels when a sampler is
  // wired: it reads the eye geometry from the final framing and returns a per-frame
  // zoom AND pitch that each only lower — the zoom widening the eye, the pitch
  // tilting it toward top-down — where the terrain would be flown into or occlude the
  // trail. The final framing mins the mode value with the clearance value on both
  // channels. Absent a sampler this is skipped and the framing is unchanged.
  const clearance = sampler
    ? resolveClearanceAdjustments(
        buildClearanceFrames(
          frameCount,
          timeline,
          totalMs,
          centerLngs,
          centerLats,
          pitches,
          zooms,
          bearings,
        ),
        route,
        sampler,
        { fps, mode: plan.mode, sampleStride: clearanceStride },
      )
    : null;

  const poses = new Array<CameraPose>(frameCount);
  for (let frame = 0; frame < frameCount; frame += 1) {
    const frameZoom = clampZoom(zooms[frame] ?? rawZooms[frame] ?? 0);
    const framePitch = pitches[frame] ?? rawPitches[frame] ?? 0;
    const clearanceZoom = clearance?.zoom[frame];
    const clearancePitch = clearance?.pitch[frame];
    poses[frame] = {
      bearing: bearings[frame] ?? rawBearings[frame] ?? 0,
      center: [
        centerLngs[frame] ?? rawBaseLngs[frame] ?? 0,
        centerLats[frame] ?? rawBaseLats[frame] ?? 0,
      ],
      pitch: clearancePitch === undefined ? framePitch : Math.min(framePitch, clearancePitch),
      zoom: clearanceZoom === undefined ? frameZoom : Math.min(frameZoom, clearanceZoom),
    };
  }

  return poses;
};

/**
 * resolveMarkerSeries returns the per-frame position the moving "you are here"
 * marker sits at — one [lng, lat] per frame, sampled exactly on the route polyline
 * rather than at the smoothed camera centre resolveCameraTrack flies. Each frame's
 * marker is the route point at the frame's hold-warped progress fraction, taken via
 * pointAtMeters(line, fraction * routeMeters, cumulative), which linearly
 * interpolates between the two vertices bracketing that arc-length — so the point
 * lies exactly on the segment between them. The marker is deliberately NOT smoothed:
 * a Gaussian low-pass on the lng/lat channels pulls the dot perpendicular off the
 * polyline on bends and hairpins, and the dot reads as "on the trail" only if it is
 * literally on it. Endpoints are pinned for free — the exact samples at fraction 0
 * and 1 are the route start and finish — and a held frame (a fraction shared with
 * its neighbours) reads the same point, so the dot freezes through the dwell exactly
 * as the centre does. The frame count is the same
 * `ceil(planDurationMs / 1000 * fps)` the track uses (markers share
 * resolveFrameProgressions with the poses and the profile samples), so markers[i]
 * aligns with the pose at frame i for the map to set per frame and for the
 * divergence budget to measure against. An empty-shots plan resolves through the
 * same full-route follow shots, so the marker sweeps the whole route start-to-end
 * even on a freshly-seeded director plan. Shared by resolveCameraTrack (its budget
 * clamp) and the public resolveRouteMarkers.
 */
const resolveMarkerSeries = (plan: CameraPlan, route: RouteGeometry): LngLat[] => {
  const line = route.line.map((coordinate): LngLat => [coordinate[0], coordinate[1]]);
  const cumulative = cumulativeMeters(line);
  const routeMeters = cumulative[cumulative.length - 1] ?? 0;

  const fractions = resolveFrameProgressions(plan);
  const frameCount = fractions.length;

  // Sample the marker exactly on the polyline at each frame's hold-warped progress
  // fraction. pointAtMeters interpolates linearly between the bracketing vertices,
  // so the result lies on the segment between them; no smoothing is applied, which
  // is what keeps the dot from drifting perpendicular off the route on a bend or
  // hairpin. Fraction 0 and 1 resolve to the route start and finish, so the
  // endpoints stay pinned without an explicit re-pin pass.
  const markers = new Array<LngLat>(frameCount);
  for (let frame = 0; frame < frameCount; frame += 1) {
    markers[frame] = pointAtMeters(line, (fractions[frame] ?? 0) * routeMeters, cumulative);
  }

  return markers;
};

/**
 * resolveRouteMarkers is the public per-frame moving-marker path, a thin wrapper
 * over resolveMarkerSeries. Every returned point lies exactly on the route polyline
 * (the marker is sampled, not smoothed), so the map's "you are here" dot sits on the
 * trail on every frame including bends and hairpins. It is a separate, additive
 * function so resolveCameraTrack's CameraPose[] return type (and its callers/tests)
 * stay untouched while the map layer reads the dot positions through one stable seam.
 */
export const resolveRouteMarkers = (plan: CameraPlan, route: RouteGeometry): LngLat[] =>
  resolveMarkerSeries(plan, route);

/**
 * clearanceStrideFor is the plan-level convenience the preview resolve uses to pick
 * the clearance sample stride, mirroring resolveFrameProgressions in taking a whole
 * (plan, route) and deriving the per-frame quantity from it. It forwards the plan's
 * frame count, the route length, and the fps + mode (which size the anticipation
 * window) to clearanceSampleStride, so a preview resolves the clearance at the coarse
 * stride that keeps the solve a sub-100 ms task while a record passes 1 for full
 * density and a byte-unchanged artifact.
 */
export const clearanceStrideFor = (plan: CameraPlan, route: RouteGeometry): number =>
  clearanceSampleStride(frameCountFor(plan), routeLengthMeters(route.line), {
    fps: plan.output.fps,
    mode: plan.mode,
  });

/**
 * posePlanSignature is a compact, stable string of ONLY the plan fields that change
 * the resolved poses: the `mode`, the whole-flight `targetDurationMs`, and the output
 * `fps` (all three size or shape the per-frame grid), plus each shot's pose-affecting
 * fields (`at`, `bearingDeg`, `bearingMode`, `holdMs`, `pitchDeg`, `speedScale`,
 * `zoom`). It deliberately EXCLUDES the encoder-only output fields
 * (`resolution`/`aspect`/`bitrateMbps`/`quality`/`format`/`preset`) and the shot `id`,
 * so a format-only edit produces the SAME signature — the memo key the tool layer
 * caches resolved poses under, which is what lets a preset or resolution change reuse
 * the cached flight instead of re-resolving it. Shot order is the plan's own
 * sorted-by-`at` order, so two plans with identical framing hash identically.
 */
export const posePlanSignature = (plan: CameraPlan): string => {
  const shots = plan.shots
    .map(
      (shot) =>
        `${shot.at},${shot.bearingDeg ?? ""},${shot.bearingMode},${shot.holdMs},${shot.pitchDeg},${shot.speedScale},${shot.zoom}`,
    )
    .join(";");

  return `${plan.mode}|${plan.targetDurationMs}|${plan.output.fps}|${shots}`;
};

/**
 * representativeStillFraction is the flight-fraction (0..1 over the frame grid) a
 * parked preview should sit at so it shows the mode's TRAVELLING framing immediately
 * rather than the wide establishing reveal of the intro hold. It parks just past the
 * intro establishing beat — the first shot's hold, as its share of the wall-clock —
 * plus a fraction of the flight remaining after it, so the framing is engaged in
 * travel whatever the intro dwell. A director or empty-shots plan has no preset style
 * to show and is still being authored, so it parks at the start (0). previewSeek maps
 * this fraction to the pose at `round(fraction * lastFrame)`, so on a hold-warped grid
 * it still lands past the frozen intro frames.
 */
export const representativeStillFraction = (plan: CameraPlan): number => {
  if (plan.shots.length === 0) {
    return 0;
  }

  const total = planDurationMs(plan);
  if (total <= 0) {
    return 0;
  }

  const introHoldMs = [...plan.shots].sort((a, b) => a.at - b.at)[0]?.holdMs ?? 0;
  const introEnd = Math.min(1, introHoldMs / total);

  return Math.min(1, introEnd + (1 - introEnd) * REPRESENTATIVE_TRAVEL_FRACTION);
};

/**
 * bearingSigmaFramesFor returns the heading-smoothing σ in frame samples: the
 * distance-domain σ for the mode (so the smoothing stays duration-invariant),
 * floored to at least MODE_BEARING_MIN_SMOOTH_SECONDS of frames (so a switchback
 * whose heading swings 180° within a few metres of trail is still smoothed in
 * time), and re-capped at a third of the frame grid so the window never swallows
 * the whole flight. Only the heading takes the time floor; the framing and centre
 * channels keep the pure distance σ.
 */
const bearingSigmaFramesFor = (
  mode: CameraMode,
  frameCount: number,
  routeMeters: number,
  fps: number,
): number => {
  const distanceFrames = sigmaFramesForMeters(
    MODE_SMOOTHING_SIGMAS[mode].bearing,
    frameCount,
    routeMeters,
  );
  const timeFloorFrames = MODE_BEARING_MIN_SMOOTH_SECONDS[mode] * fps;

  return Math.min(frameCount / 3, Math.max(distanceFrames, timeFloorFrames));
};

/**
 * sampleSeriesAt maps a smoothed series sampled on the uniform progress grid down
 * to the per-frame grid by linear interpolation at fractional sample positions:
 * `positions[i]` is the (possibly fractional) index into `series` frame i reads.
 * An integer position returns that sample exactly (the identity that keeps a
 * hold-free, speedScale-1 plan bit-identical); a fractional one lerps the two
 * bracketing samples; a position is clamped to the series bounds. An empty series
 * returns a same-length array of zeros.
 */
const sampleSeriesAt = (series: readonly number[], positions: readonly number[]): number[] => {
  const length = series.length;
  return positions.map((position) => {
    if (length === 0) {
      return 0;
    }

    const clamped = Math.min(length - 1, Math.max(0, position));
    const lo = Math.floor(clamped);
    const hi = Math.min(length - 1, lo + 1);
    const frac = clamped - lo;
    const a = series[lo] ?? 0;
    const b = series[hi] ?? a;

    return a + (b - a) * frac;
  });
};

/**
 * sampleCircularSeriesAt is sampleSeriesAt for a heading series: it interpolates
 * the two bracketing samples the short way around the circle (adding `frac` of the
 * shortest signed arc) so the per-frame mapping crosses 0/360 without a 360°
 * plunge, and returns each result in 0..360. An empty series returns a same-length
 * array of zeros.
 */
const sampleCircularSeriesAt = (
  series: readonly number[],
  positions: readonly number[],
): number[] => {
  const length = series.length;
  return positions.map((position) => {
    if (length === 0) {
      return 0;
    }

    const clamped = Math.min(length - 1, Math.max(0, position));
    const lo = Math.floor(clamped);
    const hi = Math.min(length - 1, lo + 1);
    const frac = clamped - lo;
    const a = series[lo] ?? 0;
    const b = series[hi] ?? a;
    const delta = (((b - a) % 360) + 360) % 360;
    const shortest = delta > 180 ? delta - 360 : delta;
    const result = a + shortest * frac;

    return ((result % 360) + 360) % 360;
  });
};

/**
 * addSeries returns the element-wise sum of two equal-length series. It recombines
 * the endpoint-pinned base path with the separately-smoothed lead displacement into
 * the final centre channel; a missing entry on either side contributes 0.
 */
const addSeries = (a: readonly number[], b: readonly number[]): number[] =>
  a.map((value, index) => value + (b[index] ?? 0));

/**
 * multiplySeries returns the element-wise product of a series and a per-frame
 * weight series (the lead × its endpoint taper); a missing weight contributes 1
 * (no attenuation).
 */
const multiplySeries = (values: readonly number[], weights: readonly number[]): number[] =>
  values.map((value, index) => value * (weights[index] ?? 1));

/**
 * endpointTaper builds a per-frame 0..1 envelope that is 0 at the first and last
 * frame and ramps with a smoothstep to 1 over `rampFrames` at each end, holding 1
 * across the interior. Multiplying the smoothed lead by it forces the look-at point
 * back onto the route endpoints without a subtractive chord (which would halve the
 * interior lead); the ramp spans the centre smoothing window, so the lead eases in
 * and out rather than snapping. When the frame grid is too short for two ramps the
 * envelope is the product of both smoothsteps, which still pins both ends.
 */
const endpointTaper = (frameCount: number, rampFrames: number): number[] => {
  const lastFrame = frameCount - 1;
  const ramp = Math.max(1, rampFrames);
  const out = new Array<number>(frameCount);
  for (let i = 0; i < frameCount; i += 1) {
    const rise = smoothstep(i / ramp);
    const fall = smoothstep((lastFrame - i) / ramp);
    out[i] = rise * fall;
  }

  return out;
};

/**
 * smoothstep is the C¹ Hermite ease `3t² − 2t³` clamped to [0, 1]: a smooth 0→1
 * ramp with zero slope at both ends, used to shape the lead endpoint taper.
 */
const smoothstep = (t: number): number => {
  const x = Math.min(1, Math.max(0, t));

  return x * x * (3 - 2 * x);
};

/**
 * HoldWindow is one dwell recovered from the timeline: the held `shot`, the
 * wall-clock span `[enterMs, exitMs]` it occupies (on the same `[0, totalMs]`
 * timeline the per-frame mapping reads), and its `position` in the flight (the
 * opening `intro` beat, the closing `outro` beat, or a user-placed `mid` hold).
 * The zoom shaping reads the position to pick the eased push-in / settle / breathe
 * and the shot's bearingMode to gate the shaping to following holds only.
 */
interface HoldWindow {
  enterMs: number;
  exitMs: number;
  position: "intro" | "mid" | "outro";
  shot: CameraShot;
}

/**
 * recoverHoldWindows replays the timeline's wall-clock accumulation (the same leg
 * weighting buildTimeline uses) to recover each shot's dwell as an absolute
 * `[enterMs, exitMs]` span, classifying the first sorted shot's hold as the intro
 * beat, the last sorted shot's as the outro, and any interior held shot as a mid
 * hold. Only shots with a positive holdMs contribute a window. It is a parallel
 * pass to buildTimeline so the shaping reads the dwell with the shot in hand rather
 * than matching node fractions back to shots.
 */
const recoverHoldWindows = (
  shots: readonly CameraShot[],
  targetDurationMs: number,
): HoldWindow[] => {
  const sorted = [...shots].sort((a, b) => a.at - b.at);
  const first = sorted[0];
  if (!first) {
    return [];
  }

  const lastIndex = sorted.length - 1;
  let weightTotal = 0;
  const legWeights: number[] = [];
  for (let index = 1; index < sorted.length; index += 1) {
    const from = sorted[index - 1];
    const to = sorted[index];
    if (!from || !to) {
      legWeights.push(0);

      continue;
    }

    const span = Math.max(0, to.at - from.at);
    const weight = span / Math.max(0.1, to.speedScale);
    legWeights.push(weight);
    weightTotal += weight;
  }

  const windows: HoldWindow[] = [];
  let timeMs = 0;
  if (first.holdMs > 0) {
    windows.push({
      enterMs: timeMs,
      exitMs: timeMs + first.holdMs,
      position: lastIndex === 0 ? "outro" : "intro",
      shot: first,
    });
    timeMs += first.holdMs;
  }

  for (let index = 1; index < sorted.length; index += 1) {
    const to = sorted[index];
    if (!to) {
      continue;
    }

    const weight = legWeights[index - 1] ?? 0;
    const legMs = weightTotal > 0 ? (weight / weightTotal) * targetDurationMs : 0;
    timeMs += legMs;
    if (to.holdMs > 0) {
      windows.push({
        enterMs: timeMs,
        exitMs: timeMs + to.holdMs,
        position: index === lastIndex ? "outro" : "mid",
        shot: to,
      });
      timeMs += to.holdMs;
    }
  }

  return windows;
};

/**
 * applyHoldZoomShaping adds the deliberate, eased zoom-only motion to each hold
 * window in place. Only a hand-authored non-follow dwell is exempt (a director's
 * locked or value shot stays exactly as authored); a PRESET's non-follow hold —
 * helicopter's value-bearing beats, overview's locked map frames — still gets its
 * shaping, which is part of the preset's character rather than an override of an
 * author's intent. On the intro beat the zoom opens MODE_HOLD_REVEAL_ZOOM
 * wider and pushes in to land on the establishing keyframe at the hold's end; on
 * the outro it settles MODE_HOLD_SETTLE_ZOOM wider by the final frame; a mid hold
 * breathes out and back by MID_HOLD_REVEAL_ZOOM (zero at both edges so it joins the
 * travel framing). Every offset is negative (wider) so the clearance pass that runs
 * after — and only lowers zoom — never fights the shaping. Marker, pitch, and
 * bearing are left frozen across the dwell.
 */
const applyHoldZoomShaping = (
  zooms: number[],
  shots: readonly CameraShot[],
  mode: CameraMode,
  totalMs: number,
  lastFrame: number,
): void => {
  if (totalMs <= 0 || lastFrame <= 0) {
    return;
  }

  const reveal = MODE_HOLD_REVEAL_ZOOM[mode];
  const settle = MODE_HOLD_SETTLE_ZOOM[mode];
  for (const window of recoverHoldWindows(shots, totalMs - sumHolds(shots))) {
    if (mode === "director" && window.shot.bearingMode !== "follow") {
      continue;
    }

    const dwellMs = window.exitMs - window.enterMs;
    if (dwellMs <= 0) {
      continue;
    }

    const fromFrame = Math.max(0, Math.ceil((window.enterMs / totalMs) * lastFrame));
    const toFrame = Math.min(lastFrame, Math.floor((window.exitMs / totalMs) * lastFrame));
    for (let frame = fromFrame; frame <= toFrame; frame += 1) {
      const timeMs = (frame / lastFrame) * totalMs;
      const u = Math.min(1, Math.max(0, (timeMs - window.enterMs) / dwellMs));
      const offset = holdZoomOffset(window.position, u, reveal, settle);
      zooms[frame] = (zooms[frame] ?? 0) + offset;
    }
  }
};

/**
 * holdZoomOffset returns the negative (wider) zoom delta a hold frame at eased
 * progress `u` (0 at the dwell start, 1 at its end) takes for its position: the
 * intro opens `reveal` wider and lands on 0 at the end (`-reveal * (1 - ease)`), the
 * outro starts at 0 and settles `settle` wider (`-settle * ease`), and a mid hold
 * breathes `-MID_HOLD_REVEAL_ZOOM * sin²(πu)` (zero at both edges). The ease is
 * easeInOutCubic so the motion has no velocity kink at the dwell boundaries.
 */
const holdZoomOffset = (
  position: HoldWindow["position"],
  u: number,
  reveal: number,
  settle: number,
): number => {
  if (position === "intro") {
    return -reveal * (1 - easeInOutCubic(u));
  }

  if (position === "outro") {
    return -settle * easeInOutCubic(u);
  }

  const s = Math.sin(Math.PI * u);

  return -MID_HOLD_REVEAL_ZOOM * s * s;
};

/**
 * clampCenterToMarkerBudget pulls the look-at centre back toward the athlete in
 * place wherever the planar centre→marker offset exceeds the per-frame divergence
 * budget. The budget is MAX_MARKER_OFFCENTER_FRACTION of the viewport's ground span
 * at the frame's zoom; below 0.8 of it the offset is left identical, and above it a
 * C1 radial soft-knee (`b · (0.8 + 0.2·tanh((m/b − 0.8) / 0.2))`) compresses the
 * offset toward a 1.0·budget asymptote, so the rider never leaves the frame yet the
 * clamp introduces no slope discontinuity that would need re-smoothing. The offset
 * is measured in local equirectangular metres and the same scale factor is applied
 * to the lng/lat deltas, which is exact for the small offsets in play.
 */
const clampCenterToMarkerBudget = (
  centerLngs: number[],
  centerLats: number[],
  markers: readonly LngLat[],
  zooms: readonly number[],
): void => {
  for (let frame = 0; frame < centerLngs.length; frame += 1) {
    const marker = markers[frame];
    const centerLng = centerLngs[frame];
    const centerLat = centerLats[frame];
    if (!marker || centerLng === undefined || centerLat === undefined) {
      continue;
    }

    const budget =
      MAX_MARKER_OFFCENTER_FRACTION * viewportGroundSpanMeters(zooms[frame] ?? 0, centerLat);
    if (budget <= 0) {
      continue;
    }

    const metersPerDegLng = METERS_PER_DEGREE_LAT * Math.cos(centerLat * TO_RAD);
    const dEast = (centerLng - marker[0]) * metersPerDegLng;
    const dNorth = (centerLat - marker[1]) * METERS_PER_DEGREE_LAT;
    const offset = Math.hypot(dEast, dNorth);
    const knee = BUDGET_KNEE_FRACTION * budget;
    if (offset <= knee || offset <= 0) {
      continue;
    }

    const clampedOffset =
      budget *
      (BUDGET_KNEE_FRACTION +
        BUDGET_KNEE_SPAN * Math.tanh((offset / budget - BUDGET_KNEE_FRACTION) / BUDGET_KNEE_SPAN));
    const scale = clampedOffset / offset;
    centerLngs[frame] = marker[0] + (centerLng - marker[0]) * scale;
    centerLats[frame] = marker[1] + (centerLat - marker[1]) * scale;
  }
};

/**
 * easeInOutCubic is the C¹ ease `4t³` below the midpoint and `1 − (−2t + 2)³ / 2`
 * above it, clamped to [0, 1]: a smooth 0→1 ramp that accelerates out of the start
 * and decelerates into the end with zero slope at both, used to shape the hold
 * push-in / settle without a velocity kink at the dwell boundary.
 */
const easeInOutCubic = (t: number): number => {
  const x = Math.min(1, Math.max(0, t));

  return x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2;
};

/** sumHolds totals every shot's holdMs, the wall-clock the dwells add over travel. */
const sumHolds = (shots: readonly CameraShot[]): number => {
  let holds = 0;
  for (const shot of shots) {
    holds += shot.holdMs;
  }

  return holds;
};

/** clampZoom holds a zoom within the schema's [0, 22] range, mapping NaN to a mid zoom. */
const clampZoom = (value: number): number =>
  Math.max(0, Math.min(22, Number.isFinite(value) ? value : 14));

/**
 * buildClearanceFrames assembles the per-frame channel slices the clearance pass
 * reads from the smoothed series: each frame's centre, pitch, zoom, bearing, and
 * the route-progress fraction its wall-clock time maps to (so a missing terrain
 * sample under the centre can fall back to the route's surface elevation). It is
 * the same time→progress mapping the raw sampling loop uses, recomputed here so
 * the clearance reads the fraction without the loop having stored it.
 */
const buildClearanceFrames = (
  frameCount: number,
  timeline: readonly TimelineNode[],
  totalMs: number,
  centerLngs: readonly number[],
  centerLats: readonly number[],
  pitches: readonly number[],
  zooms: readonly number[],
  bearings: readonly number[],
): ClearanceFrame[] => {
  const lastFrame = frameCount - 1;
  const frames = new Array<ClearanceFrame>(frameCount);
  for (let frame = 0; frame < frameCount; frame += 1) {
    const timeMs = lastFrame === 0 ? 0 : (frame / lastFrame) * totalMs;
    frames[frame] = {
      bearing: bearings[frame] ?? 0,
      center: [centerLngs[frame] ?? 0, centerLats[frame] ?? 0],
      fraction: progressAtTime(timeline, timeMs),
      pitch: pitches[frame] ?? 0,
      zoom: zooms[frame] ?? 0,
    };
  }

  return frames;
};

/**
 * TimelineNode is one entry in the wall-clock → progress map: at `timeMs` the
 * flight is at route progress `fraction`. The nodes are ascending in both fields
 * for travel legs; a hold contributes two nodes at the same `fraction` and
 * different `timeMs` (the dwell), so progressAtTime returns the frozen fraction
 * across the hold window.
 */
interface TimelineNode {
  fraction: number;
  timeMs: number;
}

/**
 * buildTimeline maps wall-clock milliseconds to route progress for a shot list.
 * The travelling time (`targetDurationMs`) is split across the legs between
 * consecutive shots, each leg weighted by its progress span divided by the
 * leg's `speedScale` (a faster leg consumes less of the travel budget for the
 * same distance); each shot then inserts a hold of its `holdMs` at its own
 * progress. The FIRST travel leg accelerates out of the start and the LAST
 * decelerates into the finish — a single-leg plan does both — on a trapezoidal
 * velocity profile carried by EDGE_EASE_SUBNODES sub-nodes, so the flight never
 * leaves the intro hold or hits the outro at full travel speed; the legs'
 * wall-clock boundaries are unchanged, so holds land at the same time and the
 * total duration is invariant. The result is an ascending node list
 * progressAtTime interpolates within. A single shot yields a hold-only timeline
 * at that shot's progress.
 */
const buildTimeline = (shots: readonly CameraShot[], targetDurationMs: number): TimelineNode[] => {
  const sorted = [...shots].sort((a, b) => a.at - b.at);
  const first = sorted[0];
  if (!first) {
    return [{ fraction: 0, timeMs: 0 }];
  }

  // Weight each leg by its span / speedScale so the travel budget is distributed
  // by how long each leg should take, not just its length. The leg's speedScale
  // is taken from the shot it travels toward (the scale applies to the approach).
  let weightTotal = 0;
  const legWeights: number[] = [];
  for (let index = 1; index < sorted.length; index += 1) {
    const from = sorted[index - 1];
    const to = sorted[index];
    if (!from || !to) {
      legWeights.push(0);

      continue;
    }

    const span = Math.max(0, to.at - from.at);
    const weight = span / Math.max(0.1, to.speedScale);
    legWeights.push(weight);
    weightTotal += weight;
  }

  const nodes: TimelineNode[] = [];
  let timeMs = 0;
  // Open with the first shot's hold so an intro beat dwells before travel.
  nodes.push({ fraction: first.at, timeMs });
  if (first.holdMs > 0) {
    timeMs += first.holdMs;
    nodes.push({ fraction: first.at, timeMs });
  }

  const lastLegIndex = sorted.length - 1;
  for (let index = 1; index < sorted.length; index += 1) {
    const from = sorted[index - 1];
    const to = sorted[index];
    if (!from || !to) {
      continue;
    }

    const weight = legWeights[index - 1] ?? 0;
    const legMs = weightTotal > 0 ? (weight / weightTotal) * targetDurationMs : 0;
    // Shape the edge legs: the first accelerates away from standstill, the last
    // decelerates into the finish, a lone leg does both. Sub-nodes carry the
    // quadratic within the leg's unchanged wall-clock window; interior legs stay
    // linear (their pacing is the shots' speedScale, not an edge ease).
    const ease = edgeEaseFor(index, lastLegIndex);
    if (ease && legMs > 0 && to.at !== from.at) {
      for (let sub = 1; sub <= EDGE_EASE_SUBNODES; sub += 1) {
        const u = sub / (EDGE_EASE_SUBNODES + 1);
        nodes.push({
          fraction: from.at + (to.at - from.at) * ease(u),
          timeMs: timeMs + legMs * u,
        });
      }
    }

    timeMs += legMs;
    nodes.push({ fraction: to.at, timeMs });
    if (to.holdMs > 0) {
      timeMs += to.holdMs;
      nodes.push({ fraction: to.at, timeMs });
    }
  }

  return nodes;
};

/**
 * edgeEaseFor picks the progress shaping for a travel leg: the first leg
 * accelerates out of the start, the last decelerates into the finish, a leg
 * that is both — a two-shot plan, including the empty-plan full-route fallback —
 * does both, and an interior leg is unshaped (null, linear). Every profile is a
 * trapezoid over EDGE_EASE_WINDOW_FRACTION of the leg.
 */
const edgeEaseFor = (legIndex: number, lastLegIndex: number): ((u: number) => number) | null => {
  const isFirst = legIndex === 1;
  const isLast = legIndex === lastLegIndex;
  if (isFirst && isLast) {
    return (u) => accelerateDecelerateProfile(u, EDGE_EASE_WINDOW_FRACTION);
  }

  if (isFirst) {
    return (u) => accelerateProfile(u, EDGE_EASE_WINDOW_FRACTION);
  }

  if (isLast) {
    return (u) => decelerateProfile(u, EDGE_EASE_WINDOW_FRACTION);
  }

  return null;
};

/**
 * accelerateProfile is the first-leg trapezoid: velocity ramps linearly from 0
 * over the window `a` (progress `v·u²/(2a)`, the quadratic ramp) then cruises at
 * `v = 1 / (1 − a/2)` so the leg still lands exactly on 1 at u = 1.
 */
const accelerateProfile = (u: number, a: number): number => {
  const cruise = 1 / (1 - a / 2);

  return u <= a ? (cruise * u * u) / (2 * a) : cruise * (u - a / 2);
};

/**
 * decelerateProfile is the last-leg trapezoid: cruise at `v = 1 / (1 − a/2)`
 * from the start, then velocity ramps linearly to 0 over the final window `a`
 * (progress `1 − v·(1 − u)²/(2a)`), landing exactly on 1 with zero velocity.
 */
const decelerateProfile = (u: number, a: number): number => {
  const cruise = 1 / (1 - a / 2);

  return u >= 1 - a ? 1 - (cruise * (1 - u) * (1 - u)) / (2 * a) : cruise * u;
};

/**
 * accelerateDecelerateProfile is the lone-leg trapezoid: ramp up over `a`,
 * cruise at `v = 1 / (1 − a)`, ramp down over the final `a` — so a two-shot
 * flight still eases out of the start and into the finish within one leg.
 */
const accelerateDecelerateProfile = (u: number, a: number): number => {
  const cruise = 1 / (1 - a);
  if (u <= a) {
    return (cruise * u * u) / (2 * a);
  }

  if (u >= 1 - a) {
    return 1 - (cruise * (1 - u) * (1 - u)) / (2 * a);
  }

  return cruise * (u - a / 2);
};

/**
 * progressAtTime returns the route progress at a wall-clock time by linear
 * interpolation between the bracketing timeline nodes. A time at or before the
 * first node yields its fraction; at or past the last node, the last fraction; a
 * time inside a hold (two nodes sharing a fraction) returns that frozen fraction.
 */
const progressAtTime = (nodes: readonly TimelineNode[], timeMs: number): number => {
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  if (!first || !last) {
    return 0;
  }

  if (timeMs <= first.timeMs) {
    return first.fraction;
  }

  if (timeMs >= last.timeMs) {
    return last.fraction;
  }

  for (let index = 1; index < nodes.length; index += 1) {
    const from = nodes[index - 1];
    const to = nodes[index];
    if (!from || !to) {
      continue;
    }

    if (timeMs <= to.timeMs) {
      const span = to.timeMs - from.timeMs;
      if (span <= 0) {
        return to.fraction;
      }

      const ratio = (timeMs - from.timeMs) / span;

      return from.fraction + (to.fraction - from.fraction) * ratio;
    }
  }

  return last.fraction;
};

/**
 * fullRouteFollowShots are the implicit keyframes an empty-shots plan resolves
 * through: a `follow` shot at the route start and one at the end, both with a
 * neutral cinematic pitch and a mid zoom, so a director plan with no shots yet
 * still yields a usable full-route flight for the preview. Two shots (rather than
 * one) give the timeline a leg to travel along, so the camera sweeps the whole
 * route from start to finish instead of holding at the start.
 */
const fullRouteFollowShots = (plan: CameraPlan): CameraShot[] => [
  {
    at: 0,
    bearingMode: "follow",
    holdMs: 0,
    id: `${plan.mode}-full-route-start`,
    pitchDeg: 60,
    speedScale: 1,
    zoom: 14,
  },
  {
    at: 1,
    bearingMode: "follow",
    holdMs: 0,
    id: `${plan.mode}-full-route-end`,
    pitchDeg: 60,
    speedScale: 1,
    zoom: 14,
  },
];
